import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, X, Loader2, Save, RotateCcw, 
  AlertTriangle, Trash2, Mail, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Damage types with colors
const DAMAGE_TYPES = [
  { id: 'scratch', label: 'Scratch', color: '#f59e0b', symbol: 'S' },
  { id: 'dent', label: 'Dent', color: '#ef4444', symbol: 'D' },
  { id: 'chip', label: 'Chip', color: '#8b5cf6', symbol: 'C' },
  { id: 'crack', label: 'Crack', color: '#ec4899', symbol: 'K' },
  { id: 'rust', label: 'Rust', color: '#78350f', symbol: 'R' },
  { id: 'missing', label: 'Missing Part', color: '#06b6d4', symbol: 'M' },
  { id: 'broken', label: 'Broken', color: '#dc2626', symbol: 'B' },
  { id: 'other', label: 'Other', color: '#6b7280', symbol: 'O' }
];

const SEVERITY_LEVELS = [
  { id: 'minor', label: 'Minor', color: 'bg-yellow-500' },
  { id: 'moderate', label: 'Moderate', color: 'bg-orange-500' },
  { id: 'severe', label: 'Severe', color: 'bg-red-500' }
];

const FUEL_LEVELS = [
  { id: 'empty', label: 'E', full: 'Empty' },
  { id: 'quarter', label: '1/4', full: '1/4' },
  { id: 'half', label: '1/2', full: 'Half' },
  { id: 'three_quarter', label: '3/4', full: '3/4' },
  { id: 'full', label: 'F', full: 'Full' }
];

const CONDITION_LEVELS = [
  { id: 'excellent', label: 'Excellent', color: 'text-green-500' },
  { id: 'good', label: 'Good', color: 'text-blue-500' },
  { id: 'fair', label: 'Fair', color: 'text-yellow-500' },
  { id: 'poor', label: 'Poor', color: 'text-red-500' }
];

export function VehicleInspectionButton({ bookingId, existingInspection, onComplete, customerEmail }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          existingInspection 
            ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' 
            : 'bg-zinc-800 text-white hover:bg-zinc-700'
        }`}
        data-testid="inspection-btn"
      >
        <ClipboardCheck className="w-4 h-4" />
        <span className="hidden sm:inline">
          {existingInspection ? 'View Inspection' : 'Vehicle Inspection'}
        </span>
      </button>

      <AnimatePresence>
        {showModal && (
          <VehicleInspectionModal
            bookingId={bookingId}
            existingInspection={existingInspection}
            customerEmail={customerEmail}
            onClose={() => setShowModal(false)}
            onComplete={(data) => {
              onComplete?.(data);
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function VehicleInspectionModal({ bookingId, existingInspection, customerEmail, onClose, onComplete }) {
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeDamageType, setActiveDamageType] = useState('scratch');
  const [activeSeverity, setActiveSeverity] = useState('minor');
  const [damagePoints, setDamagePoints] = useState(existingInspection?.damage_points || []);
  const [formData, setFormData] = useState({
    overall_condition: existingInspection?.overall_condition || 'good',
    exterior_notes: existingInspection?.exterior_notes || '',
    interior_notes: existingInspection?.interior_notes || '',
    mileage: existingInspection?.mileage || '',
    fuel_level: existingInspection?.fuel_level || 'half'
  });
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [emailAddress, setEmailAddress] = useState(customerEmail || '');

  const handleZoneClick = (e, zone) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newPoint = {
      id: `dp-${Date.now()}`,
      x,
      y,
      view: zone,
      damage_type: activeDamageType,
      severity: activeSeverity,
      notes: ''
    };

    setDamagePoints([...damagePoints, newPoint]);
    setSelectedPoint(newPoint);
  };

  const updatePointNotes = (pointId, notes) => {
    setDamagePoints(damagePoints.map(p => 
      p.id === pointId ? { ...p, notes } : p
    ));
    if (selectedPoint?.id === pointId) {
      setSelectedPoint({ ...selectedPoint, notes });
    }
  };

  const deletePoint = (pointId) => {
    setDamagePoints(damagePoints.filter(p => p.id !== pointId));
    if (selectedPoint?.id === pointId) setSelectedPoint(null);
  };

  const handleSave = async (andEmail = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/workshop/job/${bookingId}/inspection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          damage_points: damagePoints,
          ...formData
        })
      });

      if (res.ok) {
        toast.success('Inspection saved!');
        
        if (andEmail && emailAddress) {
          await sendInspectionEmail();
        }
        
        onComplete?.({ damage_points: damagePoints, ...formData });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  const sendInspectionEmail = async () => {
    if (!emailAddress) {
      toast.error('Please enter customer email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/api/workshop/job/${bookingId}/inspection/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailAddress })
      });

      if (res.ok) {
        toast.success('Inspection report sent! Ask customer to check spam folder if not in inbox.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getPointsForZone = (zone) => damagePoints.filter(p => p.view === zone);

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-zinc-900 rounded-2xl w-full max-w-6xl max-h-[98vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="inspection-modal"
      >
        {/* Header */}
        <div className="border-b border-zinc-800 p-3 sm:p-4 flex items-center justify-between shrink-0">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#C41E3A]" />
            Vehicle Condition Report
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Damage Type Selection - Sticky */}
          <div className="sticky top-0 bg-zinc-900/95 backdrop-blur border-b border-zinc-800 p-3 z-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-zinc-500 mr-2">Mark damage:</span>
              {DAMAGE_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setActiveDamageType(type.id)}
                  className={`px-2 py-1 rounded text-xs font-bold transition-all flex items-center gap-1 ${
                    activeDamageType === type.id
                      ? 'ring-2 ring-white scale-105'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: type.color, color: '#fff' }}
                >
                  <span className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-[10px]">
                    {type.symbol}
                  </span>
                  <span className="hidden sm:inline">{type.label}</span>
                </button>
              ))}
              <div className="ml-auto flex gap-1">
                {SEVERITY_LEVELS.map(sev => (
                  <button
                    key={sev.id}
                    onClick={() => setActiveSeverity(sev.id)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      activeSeverity === sev.id
                        ? `${sev.color} text-white`
                        : 'bg-zinc-700 text-zinc-400'
                    }`}
                  >
                    {sev.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-4">
            {/* UNIFIED CAR DIAGRAM - All views visible */}
            <div className="bg-zinc-800 rounded-xl p-4 mb-4">
              <p className="text-xs text-zinc-500 text-center mb-4">
                Click anywhere on the vehicle to mark damage • Total marks: {damagePoints.length}
              </p>
              
              <div className="grid grid-cols-3 gap-2 max-w-4xl mx-auto">
                {/* Row 1: Front */}
                <div className="col-start-2">
                  <CarZone 
                    zone="front" 
                    label="FRONT"
                    points={getPointsForZone('front')}
                    onClick={handleZoneClick}
                    selectedPoint={selectedPoint}
                    onSelectPoint={setSelectedPoint}
                  />
                </div>
                
                {/* Row 2: Left, Top/Hood, Right */}
                <div className="row-start-2">
                  <CarZone 
                    zone="left" 
                    label="LEFT SIDE"
                    points={getPointsForZone('left')}
                    onClick={handleZoneClick}
                    selectedPoint={selectedPoint}
                    onSelectPoint={setSelectedPoint}
                    orientation="vertical"
                  />
                </div>
                <div className="row-start-2">
                  <CarZone 
                    zone="top" 
                    label="TOP / ROOF"
                    points={getPointsForZone('top')}
                    onClick={handleZoneClick}
                    selectedPoint={selectedPoint}
                    onSelectPoint={setSelectedPoint}
                    isCenter
                  />
                </div>
                <div className="row-start-2">
                  <CarZone 
                    zone="right" 
                    label="RIGHT SIDE"
                    points={getPointsForZone('right')}
                    onClick={handleZoneClick}
                    selectedPoint={selectedPoint}
                    onSelectPoint={setSelectedPoint}
                    orientation="vertical"
                  />
                </div>
                
                {/* Row 3: Back */}
                <div className="col-start-2 row-start-3">
                  <CarZone 
                    zone="back" 
                    label="REAR"
                    points={getPointsForZone('back')}
                    onClick={handleZoneClick}
                    selectedPoint={selectedPoint}
                    onSelectPoint={setSelectedPoint}
                  />
                </div>
              </div>
            </div>

            {/* Selected Point Details */}
            {selectedPoint && (
              <div className="bg-zinc-800 rounded-xl p-4 mb-4 border-2 border-[#C41E3A]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: DAMAGE_TYPES.find(t => t.id === selectedPoint.damage_type)?.color }}
                    >
                      {DAMAGE_TYPES.find(t => t.id === selectedPoint.damage_type)?.symbol}
                    </div>
                    <span className="text-white font-medium">
                      {DAMAGE_TYPES.find(t => t.id === selectedPoint.damage_type)?.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      SEVERITY_LEVELS.find(s => s.id === selectedPoint.severity)?.color
                    } text-white`}>
                      {SEVERITY_LEVELS.find(s => s.id === selectedPoint.severity)?.label}
                    </span>
                    <span className="text-zinc-500 text-sm">
                      on {selectedPoint.view.toUpperCase()}
                    </span>
                  </div>
                  <button
                    onClick={() => deletePoint(selectedPoint.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  value={selectedPoint.notes || ''}
                  onChange={e => updatePointNotes(selectedPoint.id, e.target.value)}
                  placeholder="Describe this damage (e.g., 'Deep scratch near door handle')..."
                  className="w-full px-4 py-3 bg-zinc-700 border border-zinc-600 rounded-xl text-white placeholder-zinc-500"
                  autoFocus
                />
              </div>
            )}

            {/* Damage Summary */}
            {damagePoints.length > 0 && (
              <div className="bg-zinc-800 rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Damage Summary
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {damagePoints.map((point, idx) => {
                    const type = DAMAGE_TYPES.find(t => t.id === point.damage_type);
                    return (
                      <div 
                        key={point.id} 
                        className={`flex items-center gap-2 text-sm p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedPoint?.id === point.id ? 'bg-zinc-700' : 'hover:bg-zinc-700/50'
                        }`}
                        onClick={() => setSelectedPoint(point)}
                      >
                        <span 
                          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                          style={{ backgroundColor: type?.color }}
                        >
                          {idx + 1}
                        </span>
                        <span className="text-white">{type?.label}</span>
                        <span className="text-zinc-500 text-xs">{point.view}</span>
                        {point.notes && (
                          <span className="text-zinc-400 text-xs truncate flex-1">- {point.notes}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Overall Condition */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Overall Condition</label>
                <div className="grid grid-cols-2 gap-1">
                  {CONDITION_LEVELS.map(cond => (
                    <button
                      key={cond.id}
                      onClick={() => setFormData({ ...formData, overall_condition: cond.id })}
                      className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                        formData.overall_condition === cond.id
                          ? 'bg-zinc-700 ring-2 ring-[#C41E3A]'
                          : 'bg-zinc-800 hover:bg-zinc-700'
                      } ${cond.color}`}
                    >
                      {cond.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mileage */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Mileage (km)</label>
                <input
                  type="text"
                  value={formData.mileage}
                  onChange={e => setFormData({ ...formData, mileage: e.target.value })}
                  placeholder="e.g., 45,000"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm"
                />
              </div>

              {/* Fuel Level */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Fuel Level</label>
                <div className="flex gap-1">
                  {FUEL_LEVELS.map(fuel => (
                    <button
                      key={fuel.id}
                      onClick={() => setFormData({ ...formData, fuel_level: fuel.id })}
                      className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                        formData.fuel_level === fuel.id
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {fuel.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Customer Email</label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={e => setEmailAddress(e.target.value)}
                  placeholder="customer@email.com"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Exterior Notes</label>
                <textarea
                  value={formData.exterior_notes}
                  onChange={e => setFormData({ ...formData, exterior_notes: e.target.value })}
                  placeholder="Overall exterior condition, cleanliness..."
                  rows={2}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Interior Notes</label>
                <textarea
                  value={formData.interior_notes}
                  onChange={e => setFormData({ ...formData, interior_notes: e.target.value })}
                  placeholder="Interior condition, items in vehicle..."
                  rows={2}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 p-3 sm:p-4 flex flex-wrap gap-2 shrink-0 bg-zinc-900">
          <button
            onClick={() => {
              setDamagePoints([]);
              setSelectedPoint(null);
              setFormData({
                overall_condition: 'good',
                exterior_notes: '',
                interior_notes: '',
                mileage: '',
                fuel_level: 'half'
              });
            }}
            className="px-4 py-2 bg-zinc-800 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-zinc-700 text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          
          <div className="flex-1" />
          
          {existingInspection && emailAddress && (
            <button
              onClick={sendInspectionEmail}
              disabled={sendingEmail}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Email Report
            </button>
          )}
          
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 bg-zinc-700 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-zinc-600 disabled:opacity-50 text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !emailAddress}
            className="px-4 py-2 bg-[#C41E3A] text-white rounded-lg font-bold flex items-center gap-2 hover:bg-[#a01830] disabled:opacity-50 text-sm"
            data-testid="save-inspection-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Save & Email
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Individual car zone component with SVG diagram
function CarZone({ zone, label, points, onClick, selectedPoint, onSelectPoint, orientation = 'horizontal', isCenter = false }) {
  const aspectRatio = orientation === 'vertical' ? 'aspect-[1/2]' : isCenter ? 'aspect-square' : 'aspect-[2/1]';
  
  return (
    <div className="relative">
      <div 
        className={`relative bg-zinc-700/50 rounded-lg ${aspectRatio} cursor-crosshair border-2 border-zinc-600 hover:border-zinc-500 transition-colors overflow-hidden`}
        onClick={(e) => onClick(e, zone)}
      >
        {/* Zone label */}
        <div className="absolute top-1 left-1 right-1 text-center">
          <span className="text-[10px] text-zinc-400 font-medium bg-zinc-800/80 px-2 py-0.5 rounded">
            {label}
          </span>
        </div>
        
        {/* SVG Car outline for this zone */}
        <CarZoneSVG zone={zone} />
        
        {/* Damage Points */}
        {points.map((point, idx) => {
          const damageType = DAMAGE_TYPES.find(t => t.id === point.damage_type);
          const isSelected = selectedPoint?.id === point.id;

          return (
            <div
              key={point.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all ${
                isSelected ? 'z-20 scale-125' : 'z-10 hover:scale-110'
              }`}
              style={{ left: `${point.x}%`, top: `${point.y}%` }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectPoint(isSelected ? null : point);
              }}
            >
              <div 
                className={`w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-[10px] font-bold ${
                  isSelected ? 'ring-2 ring-yellow-400 ring-offset-1 ring-offset-zinc-700' : ''
                }`}
                style={{ backgroundColor: damageType?.color }}
              >
                {damageType?.symbol}
              </div>
            </div>
          );
        })}
        
        {/* Point count badge */}
        {points.length > 0 && (
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">
            {points.length}
          </div>
        )}
      </div>
    </div>
  );
}

// SVG outlines for each zone
function CarZoneSVG({ zone }) {
  const commonProps = {
    className: "absolute inset-0 w-full h-full p-2",
    fill: "none",
    stroke: "#52525b",
    strokeWidth: "1.5",
    preserveAspectRatio: "xMidYMid meet"
  };

  switch (zone) {
    case 'front':
      return (
        <svg {...commonProps} viewBox="0 0 100 50">
          <path d="M15 45 L15 25 Q15 15 25 10 L75 10 Q85 15 85 25 L85 45" />
          <ellipse cx="25" cy="30" rx="8" ry="5" /> {/* Left headlight */}
          <ellipse cx="75" cy="30" rx="8" ry="5" /> {/* Right headlight */}
          <rect x="35" y="32" width="30" height="10" rx="2" /> {/* Grille */}
          <path d="M25 10 Q50 2 75 10" /> {/* Windshield top */}
        </svg>
      );
    case 'back':
      return (
        <svg {...commonProps} viewBox="0 0 100 50">
          <path d="M15 45 L15 20 Q15 12 25 8 L75 8 Q85 12 85 20 L85 45" />
          <rect x="18" y="25" width="12" height="12" rx="2" /> {/* Left tail light */}
          <rect x="70" y="25" width="12" height="12" rx="2" /> {/* Right tail light */}
          <rect x="35" y="35" width="30" height="8" rx="2" /> {/* License plate */}
          <path d="M25 8 Q50 0 75 8" /> {/* Rear window */}
        </svg>
      );
    case 'left':
    case 'right':
      return (
        <svg {...commonProps} viewBox="0 0 50 100">
          <path d="M5 85 L5 65 L10 65 L15 35 L35 35 L42 65 L45 65 L45 85" />
          <path d="M18 38 L20 60 L38 60 L40 38 Z" /> {/* Windows */}
          <line x1="28" y1="38" x2="28" y2="80" strokeDasharray="2" /> {/* Door line */}
          <circle cx="15" cy="85" r="8" /> {/* Front wheel */}
          <circle cx="35" cy="85" r="8" /> {/* Rear wheel */}
          <ellipse cx="8" cy="50" rx="3" ry="2" /> {/* Mirror */}
        </svg>
      );
    case 'top':
      return (
        <svg {...commonProps} viewBox="0 0 100 100">
          {/* Car body outline */}
          <path d="M30 10 Q50 5 70 10 L75 25 L75 75 Q50 85 25 75 L25 25 Z" />
          {/* Windshield */}
          <path d="M32 18 Q50 12 68 18 L65 35 L35 35 Z" />
          {/* Rear window */}
          <path d="M35 65 L65 65 Q50 78 35 65" />
          {/* Side mirrors */}
          <ellipse cx="22" cy="30" rx="5" ry="3" />
          <ellipse cx="78" cy="30" rx="5" ry="3" />
          {/* Wheels */}
          <rect x="20" y="22" width="8" height="15" rx="2" />
          <rect x="72" y="22" width="8" height="15" rx="2" />
          <rect x="20" y="60" width="8" height="15" rx="2" />
          <rect x="72" y="60" width="8" height="15" rx="2" />
        </svg>
      );
    default:
      return null;
  }
}

// Display-only inspection viewer for tracking page
export function InspectionViewer({ inspection }) {
  if (!inspection) return null;

  return (
    <div className="bg-zinc-800 rounded-xl p-4 space-y-4">
      <h4 className="text-white font-medium flex items-center gap-2">
        <ClipboardCheck className="w-5 h-5 text-[#C41E3A]" />
        Pre-Service Vehicle Inspection
      </h4>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="bg-zinc-700/50 rounded-lg p-3">
          <span className="text-zinc-500 text-xs block mb-1">Condition</span>
          <span className={`font-medium ${
            CONDITION_LEVELS.find(c => c.id === inspection.overall_condition)?.color || 'text-white'
          }`}>
            {CONDITION_LEVELS.find(c => c.id === inspection.overall_condition)?.label || inspection.overall_condition}
          </span>
        </div>
        {inspection.mileage && (
          <div className="bg-zinc-700/50 rounded-lg p-3">
            <span className="text-zinc-500 text-xs block mb-1">Mileage</span>
            <span className="text-white font-medium">{inspection.mileage} km</span>
          </div>
        )}
        {inspection.fuel_level && (
          <div className="bg-zinc-700/50 rounded-lg p-3">
            <span className="text-zinc-500 text-xs block mb-1">Fuel Level</span>
            <span className="text-white font-medium">
              {FUEL_LEVELS.find(f => f.id === inspection.fuel_level)?.full || inspection.fuel_level}
            </span>
          </div>
        )}
        <div className="bg-zinc-700/50 rounded-lg p-3">
          <span className="text-zinc-500 text-xs block mb-1">Damage Points</span>
          <span className="text-white font-medium">{inspection.damage_points?.length || 0}</span>
        </div>
      </div>

      {inspection.damage_points?.length > 0 && (
        <div>
          <h5 className="text-zinc-400 text-sm mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Pre-existing Damage Recorded
          </h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {inspection.damage_points.map((point, i) => {
              const type = DAMAGE_TYPES.find(t => t.id === point.damage_type);
              const severity = SEVERITY_LEVELS.find(s => s.id === point.severity);
              return (
                <div key={i} className="flex items-center gap-2 text-sm bg-zinc-700/30 rounded-lg p-2">
                  <span 
                    className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: type?.color }}
                  >
                    {type?.symbol}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{type?.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${severity?.color} text-white`}>
                        {severity?.label}
                      </span>
                    </div>
                    <span className="text-zinc-500 text-xs">{point.view}</span>
                    {point.notes && <p className="text-zinc-400 text-xs truncate">{point.notes}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(inspection.exterior_notes || inspection.interior_notes) && (
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {inspection.exterior_notes && (
            <div className="bg-zinc-700/30 rounded-lg p-3">
              <span className="text-zinc-500 text-xs block mb-1">Exterior Notes</span>
              <p className="text-white">{inspection.exterior_notes}</p>
            </div>
          )}
          {inspection.interior_notes && (
            <div className="bg-zinc-700/30 rounded-lg p-3">
              <span className="text-zinc-500 text-xs block mb-1">Interior Notes</span>
              <p className="text-white">{inspection.interior_notes}</p>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-zinc-500 border-t border-zinc-700 pt-3">
        Inspected by {inspection.inspected_by} • {new Date(inspection.inspected_at).toLocaleString()}
      </p>
    </div>
  );
}

export default VehicleInspectionButton;
